/**
 * Account-keyed proposal generator. Embeds the Fission proposal builder as an
 * iframe, resolving the tenant/plan-scoped Fission URL from the account's
 * client config via `account.getFissionUrl`. Replaces the prospect/deal-keyed
 * Fission generator from the legacy pipeline.
 */

import {AlertTriangle} from 'lucide-react';
import {trpc} from '@common-web/lib/trpc';
import {Spinner} from '@common-web/components/ui/spinner';

export interface AccountProposalGeneratorProps {
  accountsId: string;
  title?: string;
  /**
   * Preset the embedded generator to start on Quick Proposal or Informed
   * Analysis (best-effort hint passed through to the Fission embed URL).
   */
  initialType?: 'QUICK_PROPOSAL' | 'INFORMED_ANALYSIS';
}

export function AccountProposalGenerator({
  accountsId,
  title = 'Proposal Generator',
  initialType,
}: AccountProposalGeneratorProps) {
  const {data, error, isLoading} = trpc.account.getFissionUrl.useQuery(
    {accountsId, initialType},
    {enabled: !!accountsId, refetchOnWindowFocus: false},
  );

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-full min-h-64'>
        <Spinner className='h-8 w-8' />
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center h-full min-h-64 p-4'>
        <div className='flex max-w-md items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4 text-red-900'>
          <AlertTriangle className='mt-0.5 h-5 w-5 shrink-0 text-red-600' />
          <div>
            <p className='text-sm font-semibold'>Error loading proposal generator</p>
            <p className='mt-0.5 text-sm text-red-800'>Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data?.url) {
    return (
      <div className='flex items-center justify-center h-full min-h-64 p-4'>
        <div className='flex max-w-md items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900'>
          <AlertTriangle className='mt-0.5 h-5 w-5 shrink-0 text-amber-600' />
          <div>
            <p className='text-sm font-semibold'>Proposal generation not configured</p>
            <p className='mt-0.5 text-sm text-amber-800'>
              Proposal generation is not set up for this client. Contact your
              administrator to enable it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <iframe
      className='w-full h-full border-none'
      src={data.url}
      title={title}
      allow='clipboard-read; clipboard-write;'
    />
  );
}
